import {
	createLog,
	LogActionEnum,
	LogStatusEnum,
	OrigemInformacaoEnum,
} from '../../services/api/requests/statusLogs/statusLogs';

export interface BulkCreateConfig {
	eventUrl: string;
	bulkCreateType: string;
	storageKey: string;
}

export const BULK_CREATE_CONFIGS: Record<string, BulkCreateConfig> = {
	'/bulkCreate/product': {
		eventUrl: '/bulkCreate/product',
		bulkCreateType: 'Produto',
		storageKey: 'product-upload',
	},
	'/bulkCreate/sale': {
		eventUrl: '/bulkCreate/sale',
		bulkCreateType: 'Venda',
		storageKey: 'sale-upload',
	},
	'/bulkCreate/opportunity': {
		eventUrl: '/bulkCreate/opportunity',
		bulkCreateType: 'Oportunidade',
		storageKey: 'opportunity-upload',
	},
	'/bulkCreate/orcamento': {
		eventUrl: '/bulkCreate/orcamento',
		bulkCreateType: 'Orçamento',
		storageKey: 'budget-upload',
	},
	'/bulkCreate/forecast': {
		eventUrl: '/bulkCreate/forecast',
		bulkCreateType: 'Forecast',
		storageKey: 'forecast-upload',
	},
	'/bulkCreate/estoque': {
		eventUrl: '/bulkCreate/estoque',
		bulkCreateType: 'Estoque',
		storageKey: 'stock-upload',
	},
	'/bulkCreate/order-backlog': {
		eventUrl: '/bulkCreate/order-backlog',
		bulkCreateType: 'Order Backlog',
		storageKey: 'order-backlog-upload',
	},
	'/bulkCreate/roteiroProducao': {
		eventUrl: '/bulkCreate/roteiroProducao',
		bulkCreateType: 'Roteiro Produção',
		storageKey: 'production-roadmap-upload',
	},
	'/bulkCreate/commercial-hierarchy': {
		eventUrl: '/bulkCreate/commercial-hierarchy',
		bulkCreateType: 'Hierarquia Comercial',
		storageKey: 'commercial-hierarchy-upload',
	},
};

const generateUUID = (): string => {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
		const r = (Math.random() * 16) | 0;
		const v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

export const initializeBulkCreateLog = (
	eventUrl: string,
	fileName: string
): string | null => {
	const config = BULK_CREATE_CONFIGS[eventUrl];
	if (!config) {
		return null;
	}

	const uploadUid = generateUUID();
	localStorage.setItem(`${config.storageKey}-log-uid`, uploadUid);
	localStorage.setItem(`${config.storageKey}-file-name`, fileName);

	return uploadUid;
};

export const createBulkCreateErrorLog = async (
	eventUrl: string,
	errorDetails?: {
		quantidade_erros?: number;
		message?: string;
		errors?: string;
	}
): Promise<void> => {
	const config = BULK_CREATE_CONFIGS[eventUrl];
	if (!config) {
		return;
	}

	const uploadUid = localStorage.getItem(`${config.storageKey}-log-uid`);
	const fileName = localStorage.getItem(`${config.storageKey}-file-name`);

	if (!uploadUid) {
		return;
	}

	try {
		await createLog({
			origin: OrigemInformacaoEnum.EASY,
			action: LogActionEnum.BULKCREATE,
			eventUrl: config.eventUrl,
			status: LogStatusEnum.ERROR,
			uid: uploadUid,
			details: {
				bulkCreateType: config.bulkCreateType,
				fileNameSource: fileName || 'arquivo_desconhecido.xlsx',
				...(errorDetails?.quantidade_erros
					? { errors: errorDetails.errors || '[]' }
					: {}),
				...(errorDetails?.message
					? { message: errorDetails.message }
					: {}),
			},
		});
	} catch (error) {
		console.error('Erro ao criar log de erro:', error);
	}
};

export const createBulkCreateSuccessLog = async (
	eventUrl: string
): Promise<void> => {
	const config = BULK_CREATE_CONFIGS[eventUrl];
	if (!config) {
		return;
	}

	const uploadUid = localStorage.getItem(`${config.storageKey}-log-uid`);
	const fileName = localStorage.getItem(`${config.storageKey}-file-name`);

	if (!uploadUid) {
		return;
	}

	try {
		await createLog({
			origin: OrigemInformacaoEnum.EASY,
			action: LogActionEnum.BULKCREATE,
			eventUrl: config.eventUrl,
			status: LogStatusEnum.SUCCESS,
			uid: uploadUid,
			details: {
				bulkCreateType: config.bulkCreateType,
				fileNameSource: fileName || 'arquivo_desconhecido.xlsx',
				errors: '[]',
			},
		});
	} catch (error) {
		console.error('Erro ao criar log de sucesso:', error);
	}
};

export const cleanupBulkCreateLogStorage = (eventUrl: string): void => {
	const config = BULK_CREATE_CONFIGS[eventUrl];
	if (!config) {
		return;
	}

	localStorage.removeItem(`${config.storageKey}-log-uid`);
	localStorage.removeItem(`${config.storageKey}-file-name`);
};

export const getEventUrlByModule = (module: string): string | null => {
	const moduleToEventUrlMap: Record<string, string> = {
		produto: '/bulkCreate/product',
		vendas: '/bulkCreate/sale',
		oportunidades: '/bulkCreate/opportunity',
		orcamento: '/bulkCreate/orcamento',
		forecast: '/bulkCreate/forecast',
		estoque: '/bulkCreate/estoque',
		'order-backlog': '/bulkCreate/order-backlog',
		roteiroProducao: '/bulkCreate/roteiroProducao',
		hierarquiaComercial: '/bulkCreate/commercial-hierarchy',
	};

	return moduleToEventUrlMap[module] || null;
};
